import { JournalMeasurement } from '@screens/JournalEntries/types'

const JOURNAL_MEASUREMENT_ORDER = ['mood', 'energy', 'focus', 'stress', 'appetite'] as const

type JournalMeasurementType = typeof JOURNAL_MEASUREMENT_ORDER[number]

type JournalMeasurementActionTunnelValue = number | null

export type JournalMeasurementActionTunnelDraft = {
  note: string
  occurredAt: string
  measurements: Array<{
    type: JournalMeasurementType
    value: JournalMeasurementActionTunnelValue
  }>
}

export type JournalMeasurementActionTunnelDraftUpdate = {
  note: string
  occurredAt: string
  measurements: JournalMeasurement[]
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const getJournalMeasurementActionTunnelDraft = (
  value: unknown,
): JournalMeasurementActionTunnelDraft | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const currentDraft = value as Record<string, unknown>

  return {
    note: typeof currentDraft.note === 'string' ? currentDraft.note : '',
    occurredAt: typeof currentDraft.occurred_at === 'string' ? currentDraft.occurred_at : '',
    measurements: JOURNAL_MEASUREMENT_ORDER.map((type) => ({
      type,
      value: isFiniteNumber(currentDraft[type]) ? currentDraft[type] : null,
    })),
  }
}

export const getJournalEntryMeasurementsFromActionTunnelDraft = (
  draft: JournalMeasurementActionTunnelDraft | null,
): JournalMeasurement[] =>
  draft?.measurements
    .filter(
      (measurement): measurement is JournalMeasurement & { type: JournalMeasurementType } =>
        typeof measurement.value === 'number' && measurement.value > 0,
    )
    .map((measurement) => ({
      type: measurement.type,
      value: measurement.value,
    })) ?? []

export const replaceJournalMeasurementActionTunnelDraft = (
  draft: unknown,
  update: JournalMeasurementActionTunnelDraftUpdate,
) => {
  if (!draft || typeof draft !== 'object') {
    return draft
  }

  const currentDraft = draft as Record<string, unknown>
  const measurementLookup = Object.fromEntries(
    update.measurements.map((measurement) => [measurement.type.toLowerCase(), measurement.value]),
  )

  return {
    ...currentDraft,
    note: update.note,
    occurred_at: update.occurredAt,
    ...Object.fromEntries(
      JOURNAL_MEASUREMENT_ORDER.map((type) => {
        const value = measurementLookup[type]

        return [type, typeof value === 'number' && value > 0 ? value : null]
      }),
    ),
  }
}
